import { LanguageEnum, t } from "@/i18n";
import type { AtelierKpis } from "@/schemas/AtelierKpisSchema";
import type { Atelier, AtelierListItem, CreateAtelier } from "@/schemas/atelierSchema";
import type { EmployeeKpis } from "@/schemas/EmployeeKpiSchema";
import type { CreateEmployee, Employee, EmployeeListItem } from "@/schemas/employeeSchema";
import type {
  CreateICashTransaction,
  ICashTransaction,
  ICashTransactionListItem,
} from "@/schemas/icashTransactionSchema";
import type {
  CreateIGoldTransaction,
  IGoldTransaction,
  IGoldTransactionListItem,
} from "@/schemas/igoldTransactionSchema";
import type { CreateSalary, Salary, SalaryListItem } from "@/schemas/salarySchema";
import type { UserKpis } from "@/schemas/UserKpisSchema";
import type { CreateUser, User, UserListItem } from "@/schemas/userSchema";
import type { ApiResponse, CursorPaginatedResponse } from "@/types/common";
import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import camelcaseKeys from "camelcase-keys";
import { toast } from "sonner";

const API_URL = import.meta.env.VITE_API_URL;
const MAX_REFRESH_RETRIES = 3;

interface RetryConfig extends InternalAxiosRequestConfig {
  _retryCount?: number;
  _isRefresh?: boolean;
}

const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_URL}/v1`,
  timeout: 30000,
  withCredentials: true,
});

apiClient.interceptors.response.use(
  (response) => {
    if (response.data && typeof response.data === "object") {
      response.data = camelcaseKeys(response.data, {
        deep: true,
      });
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryConfig;
    const status = error.response?.status;
    if (status === 401 && originalRequest && !originalRequest._isRefresh) {
      originalRequest._retryCount = (originalRequest._retryCount ?? 0) + 1;

      if (originalRequest._retryCount <= MAX_REFRESH_RETRIES) {
        try {
          await services.refreshToken();
          return apiClient(originalRequest);
        } catch (refreshError) {
          // refresh itself failed, fall through to error toast
        }
      } else {
        console.error(`Auth: Max refresh retries (${MAX_REFRESH_RETRIES}) reached.`);
      }
    }

    if (status) {
      const errorMap: Record<number, string> = {
        400: "errors.badRequest",
        401: "errors.unauthorized",
        403: "errors.forbidden",
        404: "errors.resourceNotFound",
        429: "errors.tooManyRequests",
        500: "errors.serverError",
      };
      const isLastAttempt = (originalRequest?._retryCount ?? 0) >= MAX_REFRESH_RETRIES;
      if (status !== 401 || isLastAttempt) {
        toast.error(t(errorMap[status] || "errors.default"));
      }
    }

    return Promise.reject(error);
  },
);

export const services = {
  // ── Auth ────────────────────────────────────────────────────────────────────
  getProfile: async (): Promise<User> => {
    const response = await apiClient.get<{
      success: boolean;
      data: User;
    }>("/profile");
    return response.data.data;
  },
  verifyCallback: async (token: string): Promise<User> => {
    const response = await apiClient.post("/auth/callback", { token });
    return response.data;
  },

  login: async (email: string, lang: LanguageEnum): Promise<void> => {
    await apiClient.post("/auth/login", { email, lang });
  },

  logout: async (): Promise<void> => {
    await apiClient.post("/auth/logout");
  },

  refreshToken: async (): Promise<User> => {
    const response = await apiClient.post<User>("/auth/refresh", null, {
      _isRefresh: true,
    } as RetryConfig);
    return response.data;
  },

  // ── User ─────────────────────────────────────────────────────────────────
  getUsers: async (params?: URLSearchParams): Promise<CursorPaginatedResponse<UserListItem>> => {
    const response = await apiClient.get<ApiResponse<CursorPaginatedResponse<UserListItem>>>(
      "/users",
      { params },
    );
    return response.data.data;
  },

  createUser: async (data: CreateUser): Promise<User> => {
    const response = await apiClient.post<ApiResponse<User>>("/users", data);
    return response.data.data;
  },

  getUser: async (id: string): Promise<User> => {
    const response = await apiClient.get<ApiResponse<User>>(`/users/${id}`);
    return response.data.data;
  },

  toggleUserActiveStatus: async (id: string): Promise<{ is_active: boolean }> => {
    const response = await apiClient.patch<ApiResponse<{ is_active: boolean }>>(
      `/users/${id}/toggle-active`,
    );
    return response.data.data;
  },

  deleteUser: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(`/users/${id}`);
    return response.data;
  },

  getUserKpis: async (id: string): Promise<UserKpis> => {
    const response = await apiClient.get<ApiResponse<UserKpis>>(`/users/${id}/kpis`);
    return response.data.data;
  },

  // ── Atelier ─────────────────────────────────────────────────────────────────
  getAteliers: async (
    params?: URLSearchParams,
  ): Promise<CursorPaginatedResponse<AtelierListItem>> => {
    const response = await apiClient.get<ApiResponse<CursorPaginatedResponse<AtelierListItem>>>(
      "/ateliers",
      { params },
    );
    return response.data.data;
  },

  createAtelier: async (data: CreateAtelier): Promise<Atelier> => {
    const response = await apiClient.post<ApiResponse<Atelier>>("/ateliers", data);
    return response.data.data;
  },

  getAtelier: async (id: string): Promise<Atelier> => {
    const response = await apiClient.get<ApiResponse<Atelier>>(`/ateliers/${id}`);
    return response.data.data;
  },

  deleteAtelier: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(`/ateliers/${id}`);
    return response.data;
  },

  getAtelierKpis: async (id: string): Promise<AtelierKpis> => {
    const response = await apiClient.get<ApiResponse<AtelierKpis>>(`/ateliers/${id}/kpis`);
    return response.data.data;
  },

  // ── Salary ──────────────────────────────────────────────────────────────────
  getEmployeeSalaries: async (
    employeeId: string,
    params?: URLSearchParams,
  ): Promise<CursorPaginatedResponse<SalaryListItem>> => {
    const response = await apiClient.get<ApiResponse<CursorPaginatedResponse<SalaryListItem>>>(
      `/employees/${employeeId}/salaries`,
      { params },
    );
    return response.data.data;
  },

  createEmployeeSalary: async (employeeId: string, data: CreateSalary): Promise<Salary> => {
    const response = await apiClient.post<ApiResponse<Salary>>(
      `/employees/${employeeId}/salaries`,
      data,
    );
    return response.data.data;
  },

  deleteEmployeeSalary: async (employeeId: string, salaryId: string): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(
      `/employees/${employeeId}/salaries/${salaryId}`,
    );
    return response.data;
  },

  // ── Employee ─────────────────────────────────────────────────────────────
  getEmployees: async (
    params?: URLSearchParams,
  ): Promise<CursorPaginatedResponse<EmployeeListItem>> => {
    const response = await apiClient.get<ApiResponse<CursorPaginatedResponse<EmployeeListItem>>>(
      "/employees",
      { params },
    );
    return response.data.data;
  },

  createEmployee: async (data: CreateEmployee): Promise<Employee> => {
    const response = await apiClient.post<ApiResponse<Employee>>("/employees", data);
    return response.data.data;
  },

  getEmployee: async (id: string): Promise<Employee> => {
    const response = await apiClient.get<ApiResponse<Employee>>(`/employees/${id}`);
    return response.data.data;
  },

  deleteEmployee: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(`/employees/${id}`);
    return response.data;
  },

  toggleEmployeeActiveStatus: async (id: string): Promise<{ is_active: boolean }> => {
    const response = await apiClient.patch<ApiResponse<{ is_active: boolean }>>(
      `/employees/${id}/toggle-active`,
    );
    return response.data.data;
  },

  getEmployeeKpis: async (id: string): Promise<EmployeeKpis> => {
    const response = await apiClient.get<ApiResponse<EmployeeKpis>>(`/employees/${id}/kpis`);
    return response.data.data;
  },

  // ── IGold Transactions ─────────────────────────────────────────────────────────────
  getIGoldTransactions: async (
    params?: URLSearchParams,
  ): Promise<CursorPaginatedResponse<IGoldTransactionListItem>> => {
    const response = await apiClient.get<
      ApiResponse<CursorPaginatedResponse<IGoldTransactionListItem>>
    >("/igold-transactions", { params });
    return response.data.data;
  },

  createIGoldTransaction: async (data: CreateIGoldTransaction): Promise<IGoldTransaction> => {
    const response = await apiClient.post<ApiResponse<IGoldTransaction>>(
      "/igold-transactions",
      data,
    );
    return response.data.data;
  },

  getIGoldTransaction: async (id: string): Promise<IGoldTransaction> => {
    const response = await apiClient.get<ApiResponse<IGoldTransaction>>(
      `/igold-transactions/${id}`,
    );
    return response.data.data;
  },

  deleteIGoldTransaction: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(`/igold-transactions/${id}`);
    return response.data;
  },

  // ── ICash Transactions ─────────────────────────────────────────────────────────────
  getICashTransactions: async (
    params?: URLSearchParams,
  ): Promise<CursorPaginatedResponse<ICashTransactionListItem>> => {
    const response = await apiClient.get<
      ApiResponse<CursorPaginatedResponse<ICashTransactionListItem>>
    >("/icash-transactions", { params });
    return response.data.data;
  },

  createICashTransaction: async (
    data: CreateICashTransaction,
  ): Promise<ICashTransactionListItem> => {
    const response = await apiClient.post<ApiResponse<ICashTransactionListItem>>(
      "/icash-transactions",
      data,
    );
    return response.data.data;
  },

  getICashTransaction: async (id: string): Promise<ICashTransaction> => {
    const response = await apiClient.get<ApiResponse<ICashTransaction>>(
      `/icash-transactions/${id}`,
    );
    return response.data.data;
  },

  deleteICashTransaction: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(`/icash-transactions/${id}`);
    return response.data;
  },
};
